/* eslint-disable */
import {
  getTokenInStorage,
  hasPublishPermission,
  getUserRole,
  saveUserRole,
} from 'services/authenticate';
import {
  USER_ROLES,
  LEADS_VIEW_OPTIONS,
  PROJECTS_DOWNLOAD_VIEW_OPTIONS,
  CALENDAR_VIEW_OPTIONS,
} from 'utils/constants';
import * as constants from './constants';
import customTaskReducer, { singleTaskReducer } from 'containers/Tasks/reducers';
import { derivePermissions } from './permissions';
import customAccountReducer from 'containers/Account/reducers';

let initialState;
initialState = {
  isAuthenticated: true,
  userInfo: {},
  siteNotifications: [],
  submissions: [],
  isAdmin: false,
  isSubmissionPermission: true,
  isForbidden: false,
  role: USER_ROLES.PRO,
  imgTags: [],
  imgColors: [],
  overAllStyles: [],
  venueType: [],
  season: [],
  showRoleSwitch: false,
  photographers: [],
  account: {
    userInfo: {},
    companyInfo: {},
    preferences: {},
  },
  leadsViewOptions: LEADS_VIEW_OPTIONS,
  projectsDownloadViewOptions: PROJECTS_DOWNLOAD_VIEW_OPTIONS,
  calendarViewOptions: CALENDAR_VIEW_OPTIONS,
  notifications: [],
  notificationCount: 0,
  siteErrors: [],
  showPausedModal: false,
  notesViewOptions: {},
  redirect: '/signin',
  notificationPreferences: {},
  userInfoErrors: [],
  userInfoLoaded: false,
  linkedAccounts: [],
};

initialState = {
  ...initialState,
  account: {
    ...initialState.account,
    subscription: {},
  }
}

// Need to validate/clean options from the db in case the db options
// has bad data written to it. There is no way to recover from a bad
// options update without this.
const validateViewOptions = (defaultViewOptions, options) => {
  // Compare default options with options in db to verify
  // db options contain all options in the default.
  const cleanOptions = defaultViewOptions
    .map((defaultOpt) => {
      // Need index of options in db to sort default options by order in db.
      const prefIndex = options?.findIndex((pref) => pref.id === defaultOpt.id);
      if (prefIndex >= 0) {
        // Copy name only from the default options. This makes it easy to update
        // labels if needed.
        // #TODO: Might need additional logic if an id of a preference needs to change.
        return {
          checked: options[prefIndex].checked,
          id: options[prefIndex].id,
          name: defaultOpt.name,
          index: prefIndex
        };
      }
      // If an option is missing, add the default.
      return defaultOpt;
  }).sort((a, b) => a.index - b.index);
  // Remove index so it does not get written into the db.
  return cleanOptions.map(opt => ({ checked: opt.checked, id: opt.id, name: opt.name }));
};

function appReducer(state = initialState, action) {
  const { checkin, notifications, tasksInfo } = action.payload || {};
  let newState = { ...state };
  switch (action.type) {
    case constants.GET_USER_INFO_FAIL:
      return { ...state, isAuthenticated: false, userInfoErrors: [...state.userInfoErrors, action.payload] }
    case constants.SET_IS_FORBIDDEN:
      return { ...state, isForbidden: true };
    case constants.SET_USER_INFO:
      const {
        id,
        email,
        username,
        firstName,
        lastName,
        profileImage,
        organization,
        organizationMember,
        admin,
        isDisabled,
        features,
        loginWeddings,
        preferences,
        isPro,
        isPaused,
        integrations,
        ...restProps
      } = action.payload;

      const { personas, menuPermissions } = derivePermissions(action.payload);

      const integrationsEmails = integrations
        .filter(integration => ['google', 'icloud', 'microsoft'].includes(integration.provider))
        .filter(integration => integration.grantStatus === 'valid')
        .sort((a, b) => b.id - a.id)
        .map(integration => integration.email);

      return {
        ...state,
        userInfoLoaded: true,
        userInfo: {
          id,
          orgId: organizationMember?.orgId,
          email,
          username,
          firstName,
          lastName,
          profileImage,
          permissions: organizationMember?.permissions,
          admin,
          features,
          isDisabled: isDisabled || false,
          loginWeddings,
          organization,
          isPro,
          personas,
          isPaused,
          integrations,
          integrationDeleting: {},
          integrationsEmails,
          savedColors: preferences?.savedColors,
          ...restProps,
        },
        isAdmin: !!admin,
        isSubmissionPermission: hasPublishPermission(organizationMember || {}),
        role: admin ? getUserRole() || USER_ROLES.PRO : USER_ROLES.PRO,
        showRoleSwitch: !!admin,
        leadsViewOptions: preferences?.leads
          ? validateViewOptions(
              LEADS_VIEW_OPTIONS,
              preferences?.leads?.map((el) => ({
                id: el.id,
                name: el.name,
                checked: el.checked,
              })),
            )
          : LEADS_VIEW_OPTIONS,
        projectsViewOptions: {
          filter: 'show-all',
          sort: preferences?.weddings?.sort || 'date',
          sortArchive: preferences?.weddings?.sortArchive || 'date',
          view: preferences?.weddings?.view || 'grid',
          location: preferences?.weddings?.location || false,
          projectLead: preferences?.weddings?.projectLead || false,
          service: preferences?.weddings?.service || false,
          leadId: 0,
        },
        calendarViewOptions: {
          firstDayOfWeek: preferences?.calendar?.firstDayOfWeek || 0,
          showChecklist: preferences?.calendar?.showChecklist || true,
          showEvents: preferences?.calendar?.showEvents || true,
          showPayments: preferences?.calendar?.showPayments || true,
          showBookings: preferences?.calendar?.showBookings || true,
          weddingExclusions: preferences?.calendar?.weddingExclusions || [],
          externalCalendarExclusions: preferences?.calendar?.externalCalendarExclusions || [],
        },
        projectsDownloadViewOptions: preferences?.weddingsDownload
          ? validateViewOptions(
              PROJECTS_DOWNLOAD_VIEW_OPTIONS,
              preferences?.weddingsDownload?.map((el) => ({
                id: el.id,
                name: el.name,
                checked: el.checked,
              })),
            )
          : PROJECTS_DOWNLOAD_VIEW_OPTIONS,
        notesViewOptions: {
          filter: preferences?.notes?.filter || 'show-all',
          view: preferences?.notes?.view || 'grid'
        },
        menuPermissions,
      };
    case constants.SET_SHOW_PAUSED_MODAL:
      return {
        ...state,
        showPausedModal: action.payload,
      };
    case constants.PUSH_SITE_NOTIFICATION:
      return {
        ...state,
        siteNotifications: [...state.siteNotifications, action.payload],
      };
    case constants.REMOVE_SITE_NOTIFICATION:
      return {
        ...state,
        siteNotifications: state.siteNotifications.filter(n => n.uuid !== action.payload),
      };
    case constants.SET_PHOTOGRAPHERS:
      const { organizations } = action.payload;
      return {
        ...state,
        photographers: organizations,
      };
    case constants.SWITCH_USER_ROLE:
      const newUserRole = (state.role === USER_ROLES.ADMIN) ? USER_ROLES.PRO : USER_ROLES.ADMIN;
      saveUserRole(newUserRole);
      return {
        ...state,
        role: newUserRole,
      };
    case constants.SHOW_ROLE_SWITCHER:
      if (!state.isAdmin) return state;
      return {
        ...state,
        showRoleSwitch: action.payload,
      };
    case constants.CHANGE_SUBMISSION:
      const { submission_id, ...otherProps } = action.payload;
      let newSubmissions = state.submissions;
      if (
        newSubmissions.findIndex((element) => element.id === submission_id) < 0
      ) {
        newSubmissions = newSubmissions.concat([
          {
            ...action.payload,
            orgId: state.userInfo.orgId,
          },
        ]);
      } else {
        newSubmissions = state.submissions.map((submission) => {
          if (submission.id === submission_id) {
            return {
              ...submission,
              ...otherProps,
            };
          }
          return submission;
        });
      }
      return {
        ...state,
        submissions: newSubmissions,
      };
    case constants.SET_LEAD_VIEW_OPTIONS:
      return {
        ...state,
        leadsViewOptions: action.payload,
      };
    case constants.SET_PROJECTS_VIEW_OPTIONS:
      return {
        ...state,
        projectsViewOptions: action.payload,
      };
    case constants.SET_CALENDAR_VIEW_OPTIONS:
      return {
        ...state,
        calendarViewOptions: action.payload,
      };
    case constants.GET_CHECKIN:
      if (checkin) {
        return {
          ...state,
          notificationCount: checkin.unreadCount + checkin.focusTaskCount,
        };
      }
      return { ...state };
    case constants.GET_NOTIFICATIONS_SUCCESS:
      if (checkin) {
        newState = { ...newState, unreadCount: checkin.unreadCount, notificationCount: checkin.unreadCount + checkin.focusTaskCount };
      }
      if (notifications) {
        newState = {
          ...newState,
          notifications: notifications.items,
          notificationsCurrentPage: notifications.currentPage,
          notificationsTotal: notifications.total,
          notificationPages: notifications.pages,
        };
      }
      if (tasksInfo) {
        newState = {
          ...newState,
          tasksInfo,
        };
      }
      return newState;
    case constants.GET_NOTIFICATIONS_FAIL:
      return {
        ...state,
        notificationCount: 0,
        notifications: [],
        notificationsCurrentPage: 0,
        notificationsTotal: 0,
        notificationPages: 0,
      };
    case constants.GET_ADDT_NOTIFICATIONS_SUCCESS:
      const { notifications: addtNotifications } = action.payload;
      return {
        ...state,
        notifications: state.notifications.concat(addtNotifications.items),
        notificationsCurrentPage: addtNotifications.currentPage,
        notificationsTotal: addtNotifications.total,
        notificationPages: addtNotifications.pages,
      };
    case constants.FETCH_SINGLE_TASK:
    // Falls through.
    case constants.FETCH_SINGLE_TASK_SUCCESS:
    // Falls through.
    case constants.FETCH_SINGLE_TASK_FAIL:
    return { ...state, tasksInfo: singleTaskReducer(state.tasksInfo, action) };
    case constants.GET_FOCUS_TASKS_SUCCESS:
    // Falls through.
    case constants.GET_TASKS_SUCCESS:
    // Falls through.
    case constants.CREATE_NEW_TASK:
    // Falls through.
    case constants.UPDATE_TASK_DETAIL:
    // Falls through.
    case constants.UPDATE_TASK_DETAIL_SUCCESS:
    // Falls through.
    case constants.DELETE_TASK:
    // Falls through.
    case constants.DELETE_TASK_SUCCESS:
    // Falls through.
    case constants.CREATE_NEW_COMMENT:
    // Falls through.
    case constants.UPDATE_COMMENT_DETAIL:
    // Falls through.
    case constants.DELETE_COMMENT:
    // Falls through.
    case constants.ADD_NEW_ASSIGNMENT:
    // Falls through.
    case constants.UPDATE_ASSIGNMENT:
    // Falls through.
    case constants.DROP_ASSIGNMENT:
    // Falls through.
    case constants.UPDATE_TASK_SUCCESS:
    // Falls through.
    case constants.TASK_REMINDER_SUCCESS:
    // Falls through.
    case constants.REORDER_TASK_COMPLETE:
    // Falls through.
    case constants.TASK_REMINDER_FAILURE:
    // Falls through.
    case constants.CREATE_TASK_REMINDER:
    // Falls through.
    case constants.UPDATE_TASK_REMINDER:
    // Falls through.
    case constants.DELETE_TASK_REMINDER:
      return { ...state, tasksInfo: customTaskReducer(state.tasksInfo, action, state.userInfo.id) };
    case constants.PUSH_SITE_ERROR:
      return {
        ...state,
        siteErrors: [...state.siteErrors, action.payload],
      };
    case constants.REMOVE_SITE_ERROR:
      return {
        ...state,
        siteErrors: state.siteErrors.filter(err => err.uuid !== action.payload),
      };
    case constants.SET_NOTES_VIEW_OPTIONS:
      return {
        ...state,
        notesViewOptions: action.payload,
      };
    case constants.PUSH_AUTH_ERROR:
      return {
        ...state,
        isAuthenticated: false,
        redirect: action.payload,
      };
    case constants.UPDATE_USER_INFO_SUCCESS:
      return {
        ...state,
        userInfo: { ...state.userInfo, ...action.payload },
        userInfoErrors: [],
      };
    case constants.UPDATE_USER_INFO_FAIL:
      return action.payload
        ? {
          ...state,
          userInfoErrors: [...state.userInfoErrors.filter(err => err.field !== action.payload.field), action.payload],
        } : state;
    case constants.GET_NOTIFICATION_PREFERENCES_SUCCESS:
      return {
        ...state,
        notificationPreferences: action.payload.notificationPreferences.prefs,
        defaultEmailBatch: action.payload.notificationPreferences.defaultEmailBatch,
      };
    case constants.UPDATE_LOGIN_SAVED_COLORS_SUCCESS:
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          savedColors: action.payload.updateLoginColorPreferences.savedColors,
        },
      };
    case constants.UPDATE_LOGIN_SAVED_COLORS_FAIL:
      // #TODO: Add error state for failure.
      return state;
    case constants.CREATE_INTEGRATION_VCAL_GRANT_SUCCESS: {
      const { integration } = action.payload;
      return { ...state, userInfo: { ...state.userInfo, integrations: [...state.userInfo.integrations, integration] } }
    };
    case constants.CREATE_INTEGRATION_VCAL_GRANT_FAIL:
      return state;
    case constants.DELETE_INTEGRATION_ACCOUNT_INIT: {
      const { id } = action.payload;
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          integrationDeleting: {
            ...state.userInfo.integrationDeleting,
            [id]: true,
          },
        },
      };
    };
    case constants.DELETE_INTEGRATION_ACCOUNT_SUCCESS: {
      const { id } = action.payload;
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          integrations: state.userInfo.integrations?.filter((el) => !(el.id === id)),
          integrationDeleting: {
            ...state.integrationDeleting,
            [id]: false,
          },
        },
      };
    };
    case constants.DELETE_INTEGRATION_ACCOUNT_FAIL: {
      const { id } = action.payload;
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          integrationDeleting: {
            ...state.integrationDeleting,
            [id]: false,
          },
        },
      };
    }
    case constants.CREATE_LOGIN_CALENDAR_SUCCESS: {
      const { loginCalendar } = action.payload;
      const addLoginCalendar = state.userInfo.integrations.map(integration => {
        if (integration.id === loginCalendar.integrationId) {
          const loginCalendars = integration.loginCalendars ? [...integration.loginCalendars, loginCalendar] : [loginCalendar];
          return { ...integration, loginCalendars };
        }
        return integration;
      });
      return { ...state, userInfo: { ...state.userInfo, integrations: addLoginCalendar } };
    };
    case constants.CREATE_LOGIN_CALENDAR_FAIL:
      return state;
    case constants.DELETE_LOGIN_CALENDAR_SUCCESS: {
      const { loginCalendar } = action.payload;
      const removeloginCalendar = state.userInfo.integrations.map(integration => {
        if (integration.id === loginCalendar.integrationId) {
          const loginCalendars = integration.loginCalendars.filter(lc => lc.id !== loginCalendar.id);
          return { ...integration, loginCalendars };
        }
        return integration;
      });
      return {
        ...state,
        userInfo: {
          ...state.userInfo,
          integrations: removeloginCalendar,
        },
      };
    }
    case constants.DELETE_LOGIN_CALENDAR_FAIL:
      return state;
    case constants.GET_LINKED_ACCOUNTS:
      return {
        ...state,
        linkedAccounts: action.payload,
      };
    default:
      return state;
  };
}

export default appReducer;
